import React, { useState, useEffect } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Paper,
  TablePagination,
} from "@mui/material";
import CustomCheckboxFilter from "../customCheckboxFilter";
import ActionIcons from "./actions";
import ScopeTags from "./scopeTag";
import "./basicCustomTable.css";
import StatusTags from "./statusTags";
import SearchFilter from "./searchFilter";
import Tooltip from "@mui/material/Tooltip";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CustomTimePicker from "../elements/customTimePicker/index";
import CustomCheckbox from "../elements/checkboxCustom";
import Tags from "./tags";
import CustomSwitch from "../elements/customSwitch";

const BasicCustomTable = ({
  columns = [],
  rows = [],
  handleFilter,
  onView = (row) => {},
  onEdit = (row) => {},
  onDelete = (row) => {},
  onAddEmpoyee = (row) => {},
  onChat = (row) => {},
  onPay = (row) => {},
  onMarkManualPaid = (row) => {},
  colorHeader = false,
  customClassHeader = "",
  tableName = "",
  enableCheckbox = false,
  onSelectedRowsChange = () => {},
  onTagChange,
  maxHeightClass = "",
  handleTimeChange,
  handleAccept,
  backendPagination = false,
  frontendPagination = false,
  totalCount = 0,
  pageLimit,
  onPageChangeRequest = () => {},
  /** When set (0-based), keeps MUI page in sync with parent/API (backend pagination). */
  paginationPageZeroBased,
  canEdit,
  canView,
  canDelete,
  onSetDefault,
}) => {
  const [page, setPage] = useState(
    typeof paginationPageZeroBased === "number" ? paginationPageZeroBased : 0
  );
  const [rowsPerPage, setRowsPerPage] = useState(pageLimit);
  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    if (
      backendPagination &&
      typeof paginationPageZeroBased === "number" &&
      paginationPageZeroBased >= 0
    ) {
      setPage(paginationPageZeroBased);
    }
  }, [backendPagination, paginationPageZeroBased]);

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (backendPagination) {
      onPageChangeRequest(1, newRowsPerPage);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (backendPagination) {
      onPageChangeRequest(newPage + 1, rowsPerPage);
    }
  };

  const isRowSelected = (id) => selectedRows.includes(id);
  const toggleRow = (id) => {
    const updatedSelection = selectedRows.includes(id)
      ? selectedRows.filter((rowId) => rowId !== id)
      : [...selectedRows, id];
    setSelectedRows(updatedSelection);
  };

  const toggleAllRows = () => {
    if (selectedRows.length === rows.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(rows.map((row) => row.id));
    }
  };

  useEffect(() => {
    onSelectedRowsChange(selectedRows);
  }, [selectedRows, onSelectedRowsChange]);

  const paginatedRows = frontendPagination
    ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : rows;

  return (
    <Paper
      elevation={2}
      className={`table-container ${
        colorHeader ? "table-container-colored" : ""
      }`}
    >
      <TableContainer
        className={`overflow-y-auto rounded-lg px-[2px] pb-[2px] ${maxHeightClass}`}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {enableCheckbox && (
                <TableCell
                  className={`table-header-cell ${customClassHeader} table-header-first !w-[40px]`}
                >
                  <div className="flex justify-center">
                    <input
                      type="checkbox"
                      checked={
                        selectedRows.length === rows.length && rows.length > 0
                      }
                      onChange={toggleAllRows}
                      className="cursor-pointer accent-[#3383B2]"
                    />
                  </div>
                </TableCell>
              )}
              {columns.map((column, index) => {
                const isPaymentHistoryActions =
                  tableName === "Payment-history" &&
                  (column.field === "action" || column.field === "actions");
                return (
                <TableCell
                  key={column.field}
                  align={isPaymentHistoryActions ? "center" : undefined}
                  className={`table-header-cell ${customClassHeader} 
                                        ${
                                          index === 0 && !enableCheckbox
                                            ? "table-header-first"
                                            : ""
                                        } 
                                        ${
                                          index === columns.length - 1
                                            ? "table-header-last"
                                            : ""
                                        }`}
                >
                  <div
                    className={`flex text-[#222222] dark:text-white ${customClassHeader}-label ${
                      isPaymentHistoryActions ? "justify-center w-full" : ""
                    }`}
                  >
                    <Typography fontWeight={600}>{column.label}</Typography>
                    {column.filter &&
                      (column.options ? (
                        <CustomCheckboxFilter
                          searchHandler={handleFilter}
                          columnLabel={column.label}
                          columnField={column.field}
                          searchFilter={[]}
                          options={column.options}
                        />
                      ) : (
                        <SearchFilter
                          searchHandler={handleFilter}
                          columnLabel={column.label}
                          columnField={column.field}
                          searchFilter={[]}
                        />
                      ))}
                  </div>
                </TableCell>
              );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (enableCheckbox ? 1 : 0)}
                  align="center"
                >
                  No Data
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row, rowIndex) => {
                const isDisabled = row.isDisabled;
                const tableRowContent = (
                  <TableRow
                    key={rowIndex}
                    className={isDisabled ? "disabled-row" : ""}
                    style={isDisabled ? { cursor: "not-allowed" } : {}}
                  >
                    {enableCheckbox && (
                      <TableCell
                        className={`table-body-cell ${customClassHeader}-body`}
                      >
                        <div className="flex justify-center">
                          <input
                            type="checkbox"
                            checked={isRowSelected(row.id)}
                            onChange={() => !isDisabled && toggleRow(row.id)}
                            disabled={isDisabled}
                            className={`accent-[#3383B2] ${
                              isDisabled
                                ? "cursor-not-allowed"
                                : "cursor-pointer"
                            }`}
                          />
                        </div>
                      </TableCell>
                    )}
                    {columns.map((column, index) => {
                      const isPaymentHistoryActions =
                        tableName === "Payment-history" &&
                        (column.field === "action" || column.field === "actions");
                      return (
                      <TableCell
                        key={column.field}
                        align={isPaymentHistoryActions ? "center" : undefined}
                        className={`table-body-cell ${customClassHeader}-body ${
                          isPaymentHistoryActions ? "table-body-cell--payment-actions" : ""
                        }`}
                        style={{
                          width: column.width,
                          ...(isPaymentHistoryActions
                            ? { verticalAlign: "middle" }
                            : {}),
                        }}
                      >
                        {/* handle special fields */}
                        {column.field === "action" || column.field === "actions" ? (
                          <ActionIcons
                            row={row}
                            onView={onView}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onAddEmpoyee={onAddEmpoyee}
                            onChat={onChat}
                            onPay={onPay}
                            onMarkManualPaid={onMarkManualPaid}
                            tableName={tableName}
                            customClass={customClassHeader}
                            canEdit={canEdit}
                            canView={canView}
                            canDelete={canDelete}
                            disabled={isDisabled}
                          />
                        ) : column.field === "status" ? (
                          (row[column.field] === "Active" || row[column.field] === "Inactive") ? (
                            <div className="flex items-center gap-2">
                              <span
                                className={`status-dot ${
                                  row[column.field] === "Active"
                                    ? "status-active"
                                    : "status-inactive"
                                }`}
                              />
                              <span
                                className={
                                  row[column.field] === "Active"
                                    ? "status-active-text"
                                    : "status-inactive-text"
                                }
                              >
                                {row[column.field]}
                              </span>
                            </div>
                          ) : (
                            <span className="dark:text-white">{row[column.field] || "—"}</span>
                          )
                        ) : column.field === "order_status" ? (
                          <StatusTags
                            rowData={row}
                            content={row[column.field]}
                            tableName={tableName}
                            onTagChange={onTagChange}
                            canUpdateStatus={row?.canUpdateStatus}
                          />
                        ) : column.field === "preferred" ? (
                          row.role === "business user" ? (
                            <CustomSwitch
                              checked={!!row[column.field]}
                              onChange={(e) => {
                                onSetDefault(e.target.checked, row);
                              }}
                            />
                          ) : (
                            <div>NA</div>
                          )
                        ) : column.field === "scopes" ? (
                          <ScopeTags
                            content={row[column.field]}
                            width="w-[200px]"
                          />
                        ) : column.field === "status_tag" ? (
                          <Tags content={row[column.field]} />
                        ) : column.field === "resources_assign" ? (
                          <div className="flex gap-2 flex-wrap">
                            {(row[column.field] || []).map((scope, index) => (
                              <ScopeTags key={index} content={scope} />
                            ))}
                          </div>
                        ) : column.field === "open_time" ||
                          column.field === "close_time" ? (
                          <div className="w-[200px]">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <CustomTimePicker
                                value={row[column.field]}
                                onChange={(val) =>
                                  !isDisabled &&
                                  handleTimeChange(row, column.field, val)
                                }
                                handleAccept={(val) =>
                                  !isDisabled &&
                                  handleAccept(row, column.field, val)
                                }
                                height={30}
                                disabled={isDisabled}
                              />
                            </LocalizationProvider>
                          </div>
                        ) : column.field === "override" ? (
                          <div className="flex justify-center">
                            <CustomCheckbox
                              checked={row[column.field]}
                              disabled={isDisabled}
                            />
                          </div>
                        ) : column.field === "paymentFlag" &&
                          (row.isCompleted || row.isFailed) ? (
                          <Tooltip
                            title={row[column.field] || ""}
                            arrow
                            placement="top"
                          >
                            <div className="dark:text-white whitespace-nowrap overflow-hidden text-ellipsis max-w-[300px]">
                              <span
                                className={
                                  row.isCompleted
                                    ? "inline-flex rounded-md px-2 py-0.5 text-sm font-semibold bg-emerald-100 text-emerald-900 dark:bg-emerald-900/45 dark:text-emerald-100"
                                    : "inline-flex rounded-md px-2 py-0.5 text-sm font-semibold bg-red-100 text-red-900 dark:bg-red-900/45 dark:text-red-100"
                                }
                              >
                                {row[column.field]}
                              </span>
                            </div>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            title={row[column.field] || ""}
                            arrow
                            placement="top"
                          >
                            <div className="dark:text-white whitespace-nowrap overflow-hidden text-ellipsis max-w-[300px]">
                              {row[column.field]}
                            </div>
                          </Tooltip>
                        )}
                      </TableCell>
                    );
                    })}
                  </TableRow>
                );

                // if row is disabled wrap with tooltip, else just render
                return isDisabled ? (
                  <Tooltip
                    key={rowIndex}
                    title="Disabled: name missing"
                    arrow
                    placement="top"
                  >
                    {tableRowContent}
                  </Tooltip>
                ) : (
                  tableRowContent
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {(frontendPagination || backendPagination) && (
        <TablePagination
          component="div"
          count={backendPagination ? totalCount : rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          className="text-[#222] dark:text-white bg-transparent"
        />
      )}
    </Paper>
  );
};

export default BasicCustomTable;
